import { useDialog } from 'muibox';
import { useContext, useState } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { SettingsContext } from '../store/SettingsContext';
import { UserContext } from '../store/UserContext';
import { handleHttpError } from '../utils/common';
import useAxios from './useAxios';

export const adminEditableServices = [
    'sextforce',
    'sextforceLimited',
    'housekeeping',
    'renameUsers',
    'inCommonFans',
    'blockUsers',
    'unfollowUsers',
] as const;

export type AdminEditableServiceName = typeof adminEditableServices[number];

export interface AdminServiceStatus {
    active?: boolean;
    paidDate?: string | null;
}

export interface AdminSubscriberFinancialDetails {
    billingName?: string;
    billingAddress?: string;
    billingCity?: string;
    billingCounty?: string;
    billingPostcode?: string;
    billingCountry?: string;
    billingCompanyName?: string;
    billingVatNumber?: string;
}

export interface AdminSubscriberGeneralDetails {
    hasLeftReview?: boolean;
    doNotContact?: boolean;
}

/**
 * The slice of an OnlyFansSubscriber the Admin can view and edit through the
 * Admin Subscriber Edit page. Mirrors the projection returned by
 * GET /subscribers/:userId/admin/details
 */
export interface AdminSubscriberDetails {
    _id: string;
    username: string;
    cognito?: { username?: string };
    email?: string;
    password?: string;
    contactEmail?: string;
    telegramChatId?: string;
    realName?: string;
    notes?: string;
    financial?: AdminSubscriberFinancialDetails;
    general?: AdminSubscriberGeneralDetails;
    sextforce?: AdminServiceStatus;
    sextforceLimited?: AdminServiceStatus;
    housekeeping?: AdminServiceStatus;
    renameUsers?: AdminServiceStatus;
    inCommonFans?: AdminServiceStatus;
    blockUsers?: AdminServiceStatus;
    unfollowUsers?: AdminServiceStatus;
}

export interface AdminSubscriberDetailsUpdate {
    username?: string;
    cognito?: { username?: string };
    email?: string;
    password?: string;
    contactEmail?: string;
    telegramChatId?: string;
    realName?: string;
    notes?: string;
    financial?: AdminSubscriberFinancialDetails;
    general?: AdminSubscriberGeneralDetails;
}

export interface AdminServiceStatusUpdate {
    active?: boolean;
    paidDate?: string | null;
}

const useSubscriberAdminDetails = (subscriberId?: string) => {
    const userContext = useContext(UserContext);
    const settingsContext = useContext(SettingsContext);
    const dialog = useDialog();
    const axios = useAxios();
    const queryClient = useQueryClient();

    const [updateDetailsLoading, setUpdateDetailsLoading] = useState<boolean>(false);
    const [updateServiceLoading, setUpdateServiceLoading] = useState<AdminEditableServiceName | null>(null);

    const queryKey = ['subscriberAdminDetails', subscriberId];

    const fetchDetails = async (): Promise<AdminSubscriberDetails | null> => {
        if (!subscriberId) {
            return null;
        }

        return axios
            .get<AdminSubscriberDetails>(`${settingsContext.routes.subscribers.base}${subscriberId}/admin/details`)
            .then(response => response.data)
            .catch(error => {
                console.error('fetchSubscriberAdminDetails', error);
                handleHttpError(error, dialog);

                return null;
            });
    };

    const details = useQuery(queryKey, fetchDetails, {
        refetchOnWindowFocus: false,
        staleTime: 1000 * 60,
        enabled: userContext.jwtToken && settingsContext.apiKey && userContext.isAdmin && subscriberId ? true : false,
    });

    /**
     * Partially updates the subscriber's details. Only the supplied (touched)
     * fields are sent and updated - everything else is left untouched.
     */
    const updateDetails = async (update: AdminSubscriberDetailsUpdate): Promise<AdminSubscriberDetails | null> => {
        if (!subscriberId) {
            return null;
        }

        setUpdateDetailsLoading(true);

        return axios
            .patch<AdminSubscriberDetails>(`${settingsContext.routes.subscribers.base}${subscriberId}/admin/details`, update)
            .then(response => {
                queryClient.setQueryData(queryKey, response.data);

                setUpdateDetailsLoading(false);

                return response.data;
            })
            .catch(error => {
                console.error('updateSubscriberAdminDetails', error);
                setUpdateDetailsLoading(false);
                handleHttpError(error, dialog);

                return null;
            });
    };

    /**
     * Updates a single service's active status and/or paid date. Only the
     * supplied (touched) fields are sent and updated.
     */
    const updateService = async (
        service: AdminEditableServiceName,
        update: AdminServiceStatusUpdate,
    ): Promise<AdminSubscriberDetails | null> => {
        if (!subscriberId) {
            return null;
        }

        setUpdateServiceLoading(service);

        return axios
            .put<AdminSubscriberDetails>(`${settingsContext.routes.subscribers.base}${subscriberId}/admin/services/${service}`, update)
            .then(response => {
                queryClient.setQueryData(queryKey, response.data);

                setUpdateServiceLoading(null);

                return response.data;
            })
            .catch(error => {
                console.error('updateSubscriberAdminService', error);
                setUpdateServiceLoading(null);
                handleHttpError(error, dialog);

                return null;
            });
    };

    return {
        ...details,
        updateDetails,
        updateDetailsLoading,
        updateService,
        updateServiceLoading,
    };
};

export default useSubscriberAdminDetails;
